import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, AlertCircle, Trash2 } from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  ActionBar,
  Button,
  BodyText,
  Card,
  MetricCard,
  METRIC_READ_CAPTIONS,
  Skeleton,
  Dialog,
  Input,
  Select,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
  ActionDefinition,
  ColumnDef,
} from '@aaimplatform/ui';
import type { Tenant, CreateTenantRequest, TenantType } from '../api/client';
import { useTenants, useCreateTenant, useDeleteTenant } from '../hooks/useTenants';
import { getTypeLabel } from '../utils/institution-labels';
import { ABSENT, formatCount, formatMoneyCompact } from '../utils/display-format';
import {
  PalStateBadge,
  TENANT_PURPOSE_OPTIONS,
  TenantPurposeBadge,
  TenantStatusBadge,
} from '../utils/status-labels';
import { RouterTableLink } from '../components/RouterTableLink';

// ---------------------------------------------------------------------------
// Summary helpers
// ---------------------------------------------------------------------------

/**
 * Sum a per-tenant figure over the tenants that report it. activeLoans and
 * totalExposure are optional on the Tenant row schema, so an absent value is
 * skipped rather than counted as a measured 0, and `reported` says how many
 * tenants the total covers.
 */
export function sumReported(values: ReadonlyArray<number | null | undefined>): {
  total: number | null;
  reported: number;
} {
  let total = 0;
  let reported = 0;
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      total += value;
      reported += 1;
    }
  }
  return { total: reported > 0 ? total : null, reported };
}

function coverageDescription(reported: number, listed: number): string | undefined {
  if (listed === 0) return 'No tenants listed';
  if (reported === 0) return undefined;
  if (reported === listed) {
    return `Across ${formatCount(listed)} listed tenant${listed === 1 ? '' : 's'}`;
  }
  return `${formatCount(reported)} of ${formatCount(listed)} listed tenants reported`;
}

// ---------------------------------------------------------------------------
// Column Definitions
// ---------------------------------------------------------------------------

function useColumns(): ColumnDef<Tenant, unknown>[] {
  const navigate = useNavigate();
  return useMemo<ColumnDef<Tenant, unknown>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Institution',
      // The kit TableLink: a real link, in the secondary color. A red
      // <button> here set every institution name in the CTA color.
      cell: ({ row }) => (
        <div>
          <RouterTableLink to={`/tenants/${row.original.id}`} className="text-sm leading-normal">
            {row.original.name}
          </RouterTableLink>
          <div className="text-xs font-sans text-tertiary leading-normal">
            {row.original.primaryContact ?? ''}
          </div>
        </div>
      ),
      size: 240,
    },
    {
      // Tenant.charterNumber is optional and omitted for a tenant with no
      // charter on file (ruling absence-representable-contracts). That prints
      // the absence marker, as data in a data color; the value was never a CTA.
      accessorKey: 'charterNumber',
      header: 'Charter #',
      cell: ({ getValue }) => {
        const charter = getValue() as string | undefined;
        return charter ? (
          <span className="text-sm font-mono text-primary leading-normal">{charter}</span>
        ) : (
          <span className="text-sm font-sans text-tertiary leading-normal">{ABSENT}</span>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ getValue }) => (
        <span className="text-sm font-sans text-secondary leading-normal">
          {getTypeLabel(getValue() as string)}
        </span>
      ),
      size: 140,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => <TenantStatusBadge status={getValue() as string} />,
      size: 110,
    },
    {
      // Tenant.purpose is required on the row (operator ruling 2026-10-22):
      // a DEMO or PROBE tenant reads as such wherever it is listed.
      accessorKey: 'purpose',
      header: 'Purpose',
      cell: ({ getValue }) => <TenantPurposeBadge purpose={getValue() as string} />,
      size: 110,
    },
    {
      accessorKey: 'palEnabled',
      header: 'PAL',
      // The PAL cell is the row's entitlements affordance. The Tenant row
      // schema carries palEnabled and none of the other entitlement flags
      // (enableLosPreview, enablePeValuation and enableLoanParticipations live
      // behind GET /tenants/{id}/entitlements). The former Entitlements column
      // printed "LOS PE LN" identically on every row with no state and
      // repeated this PAL value, so it was removed; the other flags are read
      // and edited on the Entitlements tab this cell opens.
      cell: ({ row, getValue }) => {
        const enabled = getValue() as boolean;
        const state = enabled ? 'Enabled' : 'Disabled';
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/tenants/${row.original.id}?tab=entitlements`);
            }}
            className="inline-flex cursor-pointer hover:opacity-80 transition-opacity"
            title="Manage entitlements"
            // The accessible name leads with the visible state (WCAG 2.5.3
            // Label in Name); an aria-label naming only the action hid it.
            aria-label={`PAL ${state.toLowerCase()} for ${row.original.name}. Manage entitlements`}
          >
            <PalStateBadge enabled={enabled} />
          </button>
        );
      },
      size: 100,
    },
    {
      // activeLoans and totalExposure are optional on the Tenant row schema.
      // An absent value prints "--"; it is never coerced to a measured 0.
      id: 'activeLoans',
      accessorFn: (row) => row.activeLoans,
      header: 'Loans',
      meta: { align: 'right' },
      cell: ({ getValue }) => (
        <span className="text-sm font-mono text-primary tabular-nums leading-normal">
          {formatCount(getValue() as number | undefined)}
        </span>
      ),
      size: 90,
    },
    {
      id: 'totalExposure',
      accessorFn: (row) => row.totalExposure,
      header: 'Exposure',
      meta: { align: 'right' },
      cell: ({ getValue }) => (
        <span className="text-sm font-mono text-primary tabular-nums leading-normal">
          {formatMoneyCompact(getValue() as number | undefined)}
        </span>
      ),
      size: 130,
    },
  ], [navigate]);
}

// ---------------------------------------------------------------------------
// Institution types (the filter and the create form share one list)
// ---------------------------------------------------------------------------

/**
 * Every InstitutionType, in display order. Keyed by the generated union, so a
 * type added to platform-admin.openapi.yaml fails tsc here until it is
 * listed. The Institution Type filter used to offer only CREDIT_UNION and
 * COMMUNITY_BANK while the list held Fintech, Trust Company and Other tenants.
 */
const INSTITUTION_TYPE_ORDER: Record<TenantType, true> = {
  CREDIT_UNION: true,
  COMMUNITY_BANK: true,
  REGIONAL_BANK: true,
  FINTECH: true,
  BROKER_DEALER: true,
  INSURANCE: true,
  TRANSFER_AGENT: true,
  TRUST_COMPANY: true,
  OTHER: true,
};

export const INSTITUTION_TYPE_OPTIONS: ReadonlyArray<{ value: TenantType; label: string }> =
  (Object.keys(INSTITUTION_TYPE_ORDER) as TenantType[]).map((value) => ({
    value,
    label: getTypeLabel(value),
  }));

// ---------------------------------------------------------------------------
// Filter Definitions
// ---------------------------------------------------------------------------

/**
 * Every filter here is applied in filteredTenants. The LOS Preview, PE
 * Valuation and Loan Participations filters were removed: the Tenant list
 * response carries none of those flags, so selecting one showed an active
 * filter chip over an unfiltered list.
 */
export const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'PENDING', label: 'Pending' },
      { value: 'SUSPENDED', label: 'Suspended' },
    ],
  },
  {
    id: 'type',
    label: 'Institution Type',
    type: 'select',
    options: [...INSTITUTION_TYPE_OPTIONS],
  },
  {
    id: 'palEnabled',
    label: 'PAL Status',
    type: 'select',
    options: [
      { value: 'true', label: 'PAL Enabled' },
      { value: 'false', label: 'PAL Disabled' },
    ],
  },
  {
    id: 'name',
    label: 'Institution Name',
    type: 'text',
    placeholder: 'Search by name or charter number...',
  },
];

// ---------------------------------------------------------------------------
// TenantsPage Component
// ---------------------------------------------------------------------------

/**
 * Tenant Management Page -- Modernized with DataGrid, FilterBar, and ActionBar.
 *
 * Fetches tenant data from GET /api/admin/tenants (Prisma-backed).
 * Supports creating new tenants via POST /api/admin/tenants.
 */
export function TenantsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [selectedRows, setSelectedRows] = useState<Tenant[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const columns = useColumns();
  const deleteTenant = useDeleteTenant();

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const { data: response, isLoading, error } = useTenants();
  const tenants: Tenant[] = response?.data ?? [];

  // -------------------------------------------------------------------------
  // Filtered data based on search and filters
  // -------------------------------------------------------------------------

  const filteredTenants = useMemo(() => {
    let result = [...tenants];

    // Apply search query (client-side for instant feedback)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(tenant =>
        tenant.name.toLowerCase().includes(query) ||
        !!tenant.charterNumber?.toLowerCase().includes(query) ||
        (tenant.primaryContact ?? '').toLowerCase().includes(query)
      );
    }

    // Apply filters
    for (const filter of activeFilters) {
      switch (filter.filterId) {
        case 'status':
          result = result.filter(t => t.status === filter.value);
          break;
        case 'type':
          result = result.filter(t => t.type === filter.value);
          break;
        case 'palEnabled':
          result = result.filter(t => String(t.palEnabled) === filter.value);
          break;
        case 'name':
          result = result.filter(t =>
            t.name.toLowerCase().includes(filter.value.toLowerCase()) ||
            !!t.charterNumber?.toLowerCase().includes(filter.value.toLowerCase())
          );
          break;
      }
    }

    return result;
  }, [tenants, searchQuery, activeFilters]);

  // -------------------------------------------------------------------------
  // Bulk action handlers
  // -------------------------------------------------------------------------

  const handleDelete = useCallback(async () => {
    for (const row of selectedRows) {
      await deleteTenant.mutateAsync(row.id);
    }
    setSelectedRows([]);
    setShowDeleteDialog(false);
  }, [selectedRows, deleteTenant]);

  // Delete is the one bulk action a route backs (useDeleteTenant). Activate,
  // Export and Suspend were wired to comment-only handlers and did nothing
  // when clicked; they return when a tenant-status route backs them. The
  // grid toolbar's Export (enableExport) exports the list.
  const bulkActions: ActionDefinition[] = useMemo(() => [
    { id: 'delete', label: 'Delete', icon: Trash2, variant: 'danger' as const, onClick: () => setShowDeleteDialog(true) },
  ], []);

  // -------------------------------------------------------------------------
  // Summary metrics
  // -------------------------------------------------------------------------

  const activeTenants = useMemo(
    () => filteredTenants.filter(t => t.status === 'ACTIVE').length,
    [filteredTenants],
  );

  const totalLoans = useMemo(
    () => sumReported(filteredTenants.map(t => t.activeLoans)),
    [filteredTenants],
  );

  const totalExposure = useMemo(
    () => sumReported(filteredTenants.map(t => t.totalExposure)),
    [filteredTenants],
  );

  const palEnabledCount = useMemo(
    () => filteredTenants.filter(t => t.palEnabled).length,
    [filteredTenants],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      {/* Action Buttons (top-level page actions). The list export is the grid
          toolbar's Export; a second Export here had no handler. */}
      <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
        <Button variant="primary" icon={PlusIcon} onClick={() => setShowCreateDialog(true)}>
          Onboard Tenant
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load tenants: {error.message}
          </div>
        </div>
      )}

      {/* Summary Metrics. Two per row on a phone, as on the Dashboard and
          Tenant Detail; one per row pushed the grid below the fold. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-6">
              <Skeleton className="h-10 w-12 mb-3" />
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-3 w-16" />
            </Card>
          ))
        ) : error ? (
          // The list read failed, so no tile has a figure. The empty list's
          // zeros would present an unread list as a measured one.
          <>
            <MetricCard label="Total Tenants" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Active Loans" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Total Exposure" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="PAL Enabled" value="--" caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard
              label="Total Tenants"
              value={formatCount(filteredTenants.length)}
              description={`${formatCount(activeTenants)} active`}
            />
            <MetricCard
              label="Active Loans"
              value={formatCount(totalLoans.total)}
              caption={totalLoans.reported === 0 && filteredTenants.length > 0 ? METRIC_READ_CAPTIONS.notReported : undefined}
              description={coverageDescription(totalLoans.reported, filteredTenants.length)}
            />
            <MetricCard
              label="Total Exposure"
              value={formatMoneyCompact(totalExposure.total)}
              caption={totalExposure.reported === 0 && filteredTenants.length > 0 ? METRIC_READ_CAPTIONS.notReported : undefined}
              description={coverageDescription(totalExposure.reported, filteredTenants.length)}
            />
            <MetricCard
              label="PAL Enabled"
              value={formatCount(palEnabledCount)}
              description={filteredTenants.length > 0
                ? `${Math.round((palEnabledCount / filteredTenants.length) * 100)}% of listed tenants`
                : 'No tenants listed'}
            />
          </>
        )}
      </div>

      {/* Filter Bar */}
      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search tenants by name, charter number, or contact..."
        className="rounded-t-sm"
      />

      {/* Action Bar (visible when rows selected) */}
      <ActionBar
        selectedCount={selectedRows.length}
        totalCount={filteredTenants.length}
        actions={bulkActions}
        onSelectAll={() => setSelectedRows([...filteredTenants])}
        onDeselectAll={() => setSelectedRows([])}
      />

      {/* Data Grid */}
      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<Tenant>
          data={filteredTenants}
          columns={columns}
          enableRowSelection
          enableMultiRowSelection
          onSelectionChange={setSelectedRows}
          enablePagination
          defaultPageSize={25}
          enableExport
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.id}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}

      {/* Create Tenant Dialog */}
      <CreateTenantDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Tenants"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="primary" className="bg-error hover:bg-error/90" onClick={handleDelete} disabled={deleteTenant.isPending}>
              {deleteTenant.isPending ? 'Deleting...' : `Delete ${selectedRows.length} Tenant${selectedRows.length === 1 ? '' : 's'}`}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <BodyText variant="ui">
            This will deactivate the following tenant{selectedRows.length === 1 ? '' : 's'}:
          </BodyText>
          <ul className="list-disc pl-5 space-y-1">
            {selectedRows.map(row => (
              <li key={row.id} className="text-sm font-sans text-primary">{row.name}</li>
            ))}
          </ul>
          <BodyText variant="ui" className="text-sm text-secondary">
            Tenant data will be preserved for audit purposes but the tenant will be marked inactive.
          </BodyText>
        </div>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Tenant Dialog
// ---------------------------------------------------------------------------

function CreateTenantDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createTenant = useCreateTenant();
  const [formData, setFormData] = useState<CreateTenantRequest>({
    name: '',
    charterNumber: '',
    type: 'CREDIT_UNION',
    primaryContact: '',
    palEnabled: false,
    credentialAuthEnabled: false,
    purpose: 'CUSTOMER',
  });
  const [formError, setFormError] = useState('');

  const handleFieldChange = (field: keyof CreateTenantRequest, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setFormError('Institution name is required.');
      return;
    }
    if (!formData.charterNumber.trim()) {
      setFormError('Charter number is required.');
      return;
    }

    try {
      await createTenant.mutateAsync(formData);
      // Reset form and close dialog on success
      setFormData({
        name: '',
        charterNumber: '',
        type: 'CREDIT_UNION',
        primaryContact: '',
        palEnabled: false,
        credentialAuthEnabled: false,
        purpose: 'CUSTOMER',
      });
      setFormError('');
      onOpenChange(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create tenant.');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      charterNumber: '',
      type: 'CREDIT_UNION',
      primaryContact: '',
      palEnabled: false,
      credentialAuthEnabled: false,
      purpose: 'CUSTOMER',
    });
    setFormError('');
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Onboard New Tenant"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createTenant.isPending}
          >
            {createTenant.isPending ? 'Creating...' : 'Create Tenant'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Error message */}
        {formError && (
          <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <span className="text-sm font-sans text-error">{formError}</span>
          </div>
        )}

        {/* The kit controls tie each label to its field (htmlFor/id), so every
            field has an accessible name. The raw label/input pairs had none. */}
        <Input
          label="Institution Name"
          value={formData.name}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          placeholder="Legal name of the institution"
        />

        <Input
          label="Charter Number"
          value={formData.charterNumber}
          onChange={(e) => handleFieldChange('charterNumber', e.target.value)}
          placeholder="Charter or certificate number"
          className="font-mono"
        />

        <Select
          label="Institution Type"
          value={formData.type}
          onChange={(e) => handleFieldChange('type', e.target.value)}
          options={[...INSTITUTION_TYPE_OPTIONS]}
        />

        {/* Tenant purpose (operator ruling 2026-10-22), set once at
            provisioning and forwarded to every leg of the saga. CUSTOMER is
            the spec default. The definitions are the TenantPurpose
            descriptions in platform-admin.openapi.yaml; the exclusion of the
            other values from operator metrics and FI directories is #4097. */}
        <Select
          label="Tenant Purpose"
          value={formData.purpose ?? 'CUSTOMER'}
          onChange={(e) => handleFieldChange('purpose', e.target.value)}
          options={[...TENANT_PURPOSE_OPTIONS]}
          helperText="Customer: a contracted institution. Pilot: a pilot participant. Demo: shows the product or exercises a flagged feature. Probe: a production proof's throwaway, removed in the same run."
        />

        <Input
          type="email"
          label="Primary Contact Email"
          value={formData.primaryContact ?? ''}
          onChange={(e) => handleFieldChange('primaryContact', e.target.value)}
          placeholder="admin@institution.org"
        />

        {/* PAL Enabled */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="palEnabled"
            checked={formData.palEnabled}
            onChange={(e) => handleFieldChange('palEnabled', e.target.checked)}
            className="w-4 h-4 rounded-sm border-recessed text-accent focus:ring-accent"
          />
          <label htmlFor="palEnabled" className="text-sm font-sans text-secondary">
            Enable Pledged-Asset Lending (PAL)
          </label>
        </div>

        {/* Credential Auth Enabled (ADR-011 / rbac-03c) */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="credentialAuthEnabled"
            checked={formData.credentialAuthEnabled ?? false}
            onChange={(e) => handleFieldChange('credentialAuthEnabled', e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded-sm border-recessed text-accent focus:ring-accent"
          />
          <label htmlFor="credentialAuthEnabled" className="text-sm font-sans text-secondary">
            Enable credential (password + TOTP) login
            <span className="block text-caption-sm text-tertiary leading-snug mt-0.5">
              For a tenant whose staff have no federated sign-in. Leave off for
              institutions whose staff authenticate through their own identity
              provider.
            </span>
          </label>
        </div>
      </div>
    </Dialog>
  );
}
